import { clearLocalSession, getLocalSession, loadLocalState, localJoin, reduceLocal } from './local-repository';
import type { AppAction, AppState, Restaurant } from './types';

const useRemote = import.meta.env.PROD && import.meta.env.VITE_LOCAL_ONLY !== 'true';
const TOKEN_KEY = 'fan-dazi.remote-token.v1';
const DEVICE_KEY = 'fan-dazi.device-id.v1';
const AUTO_RESUME_DISABLED_KEY = 'fan-dazi.auto-resume-disabled.v1';
const configuredApiBase = String(import.meta.env.VITE_API_BASE_URL ?? '').trim();
const isCapacitorAndroidShell = typeof window !== 'undefined'
  && window.location.protocol === 'https:'
  && window.location.hostname === 'localhost';
const apiBase = (configuredApiBase || (isCapacitorAndroidShell ? 'https://625eat.top' : '.')).replace(/\/$/, '');

function getDeviceId(): string {
  let value = localStorage.getItem(DEVICE_KEY);
  if (!value) {
    value = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, value);
  }
  return value;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  let response: Response;
  try {
    response = await fetch(`${apiBase}/api/${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new Error('服务器响应超时，请检查网络后重试');
    throw new Error('无法连接服务器，请检查网络后重试');
  } finally {
    window.clearTimeout(timeout);
  }
  let body: { success: boolean; data?: T; error?: { message: string } };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    throw new Error('服务器返回异常，请稍后重试');
  }
  if (!response.ok || !body.success || body.data === undefined) throw new Error(body.error?.message ?? '请求失败');
  return body.data;
}

export function hasSession(): boolean {
  return useRemote ? Boolean(localStorage.getItem(TOKEN_KEY)) : Boolean(getLocalSession());
}

export async function join(code: string, displayName: string, avatar: string, avatarColor: string): Promise<AppState> {
  if (!useRemote) {
    if (code.trim() !== '625') throw new Error('体验邀请码为 625');
    return localJoin(displayName, avatar, avatarColor);
  }
  const result = await request<{ token: string; state: AppState }>('join', {
    method: 'POST', body: JSON.stringify({ code, displayName, avatar, avatarColor, deviceId: getDeviceId() }),
  });
  localStorage.setItem(TOKEN_KEY, result.token);
  localStorage.removeItem(AUTO_RESUME_DISABLED_KEY);
  return result.state;
}

export async function restoreSession(): Promise<AppState | null> {
  if (!useRemote) return hasSession() ? getState() : null;
  if (localStorage.getItem(AUTO_RESUME_DISABLED_KEY) === 'true') return null;
  if (localStorage.getItem(TOKEN_KEY)) {
    try {
      const current = await getState();
      await request('device', { method: 'POST', body: JSON.stringify({ deviceId: getDeviceId() }) }).catch(() => undefined);
      return current;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    }
  }
  try {
    const result = await request<{ token: string; state: AppState }>('resume', {
      method: 'POST', body: JSON.stringify({ deviceId: getDeviceId() }),
    });
    localStorage.setItem(TOKEN_KEY, result.token);
    return result.state;
  } catch {
    return null;
  }
}

export async function getNearbyRestaurants(latitude: number, longitude: number, radius = 10_000, keyword = '', mode: 'fast' | 'full' = 'fast'): Promise<Restaurant[]> {
  const params = new URLSearchParams({
    lat: String(latitude),
    lon: String(longitude),
    radius: String(radius),
    mode,
  });
  if (keyword.trim()) params.set('q', keyword.trim());
  return request<Restaurant[]>(`restaurants/nearby?${params.toString()}`);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('照片读取失败'));
    reader.readAsDataURL(blob);
  });
}

export async function uploadMealPhoto(blob: Blob): Promise<string> {
  if (!useRemote) return blobToDataUrl(blob);
  const target = await request<{ uploadUrl: string; photoKey: string }>('photos/upload-url', {
    method: 'POST', body: JSON.stringify({ contentType: 'image/jpeg', size: blob.size }),
  });
  const uploaded = await fetch(target.uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': 'image/jpeg' },
    body: blob,
  });
  if (!uploaded.ok) throw new Error('照片上传失败，请重试');
  return target.photoKey;
}

export async function getMealPhotoUrl(photoKey: string): Promise<string> {
  if (photoKey.startsWith('data:')) return photoKey;
  const token = localStorage.getItem(TOKEN_KEY);
  const response = await fetch(`${apiBase}/api/photos/${encodeURIComponent(photoKey)}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error('照片加载失败');
  return URL.createObjectURL(await response.blob());
}

export async function getState(): Promise<AppState> {
  if (!useRemote) {
    const state = loadLocalState();
    if (!state) throw new Error('请重新加入');
    return state;
  }
  return request<AppState>('state');
}

export async function dispatch(state: AppState, action: AppAction): Promise<AppState> {
  if (!useRemote) return reduceLocal(state, action);
  return request<AppState>('action', { method: 'POST', body: JSON.stringify(action) });
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.setItem(AUTO_RESUME_DISABLED_KEY, 'true');
  clearLocalSession();
}
